import { List, styled } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { useMemo } from 'react';

import { Section, Skeleton, Button, ButtonsContainer } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { PrototypesIcon } from '@app-velocity/icons';

import { useCurrentTeamPermissions } from '@app-velocity/libs/team-permissions';

import { MessageBus } from '@app-velocity/message-bus';

import { NoScenarios } from './components/NoScenarios';
import { SingleScenario } from './components/SingleScenario';
import { ScenarioListContext } from './context/ScenarioListContext';
import { useGetPrototypesInfiniteQuery } from '../../queries/useGetPrototypesInfiniteQuery';
import { useDeleteScenarioMutation } from '../../queries/useDeleteScenarioMutation';
import { ScenarioRoutePaths } from '../../routes/ScenariosRoutePaths';

//@TODO should be placed in some shared resource

const ScenarioList = styled(List)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export type ChallengesProps = {
  fileId?: string;
};

const teamsMessageBus = new MessageBus('teams');

export const Challenges = ({ fileId }: ChallengesProps) => {
  const { teamId, workspaceId } = useParams<{
    teamId: string;
    workspaceId: string;
  }>();
  const currentTeam = useCurrentTeamPermissions();
  const { t } = useI18n();
  const {
    data: scenarioPages,
    isLoading: isLoadingAllScenarios,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPrototypesInfiniteQuery(workspaceId, fileId, { owner: 'own' });
  const scenarios = useMemo(
    () => scenarioPages?.pages.flatMap((page) => page.content) ?? [],
    [scenarioPages]
  );
  const navigate = useNavigate();

  const { mutate: removeScenario, isPending } =
    useDeleteScenarioMutation(workspaceId);

  const addNewScenario = () => {
    if (!currentTeam) return;

    const higherTieredTeam = currentTeam.getHigherTieredTeam();

    if (higherTieredTeam) {
      teamsMessageBus.publish('modal:paid-team-available:open', {
        team: higherTieredTeam,
        redirectToSelectFlow: true,
        onModalCloseCallback: () => {
          navigate(ScenarioRoutePaths.challengeAFlow(teamId!, workspaceId!));
        },
      });

      return;
    }

    if (!currentTeam.canRunSimulations(1)) {
      return;
    }

    navigate(ScenarioRoutePaths.challengeAFlow(teamId!, workspaceId!));
  };

  const isLoading = isLoadingAllScenarios || isPending;

  if (isLoading)
    return (
      <Section>
        <Skeleton height={53} borderRadius={1} margin="0" />
        <Skeleton height={53} borderRadius={1} margin="0" />
        <Skeleton height={53} borderRadius={1} margin="0" />
      </Section>
    );

  if (!scenarios.length)
    return (
      <Section>
        <NoScenarios />
      </Section>
    );

  return (
    <Section
      header={
        <Section.StickyHeader
          title="prototypes:prototype_challenges"
          right={
            <Button
              data-ph-capture-attribute-id="create_challenge_button"
              onClick={addNewScenario}
              variant="contained"
              color="primary"
              size="small"
            >
              {t('prototypes:challenge_a_flow')}
              <PrototypesIcon />
            </Button>
          }
        />
      }
    >
      <ScenarioListContext.Provider value={{ removeScenario }}>
        <ScenarioList disablePadding dense>
          {scenarios?.map((scenario) => (
            <SingleScenario key={scenario!.id} scenario={scenario!} />
          ))}
        </ScenarioList>
      </ScenarioListContext.Provider>
      {hasNextPage ? (
        <ButtonsContainer align="center">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {t('shared:show_more')}
          </Button>
        </ButtonsContainer>
      ) : null}
    </Section>
  );
};
